import React, { useEffect, useState } from "react";
import {
  Grid,
  Typography,
  Box,
  Card,
  Skeleton,
  CardMedia,
  CardActions,
  Button,
  Modal,
  IconButton,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import axios from "axios";
import { useSelector } from "react-redux";

import {
  app_title,
  defaultUrlSegment,
  endpoint,
  rupiCardURL,
  urlSegmentRegex,
  zetcardURL,
} from "../config";
import productListApi from "../hooks/GET/productList";
import getPartnerIDByPartnerName from "../hooks/GET/getPartnerIDByPartnerName";
import { customToast } from "../lib/toastLib";
import {
  getJWTTokenFromCookie,
  getLeadFromCookie,
  getPartnerIdFromCookie,
  getProspectAppIdFromCookie,
  isPartnerAvailable,
  isPrimaryDomain,
  saveCreditCardDetailsInCookie,
  savePartnerIdCookie,
  saveProductIdInCookie,
  saveProductTypeInCookie,
  saveUTMsCookie,
  saveUrlSegmentInCookie,
} from "../utils";
import CloseIcon from "@mui/icons-material/Close";
import BussinessGSTModal from "../components/BussinessGSTModal";
import Loading from "../components/Loading";
import HashLogin from "../components/HashLogin";
import { partnerIdByUrlSegment } from "../hooks/GET/partnerIdByUrlSegment";

function Products() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const productsType = process.env.REACT_APP_PRODUCT_TYPE;
  const lenderType = process.env.REACT_APP_LENDER_TYPE;

  if (lenderType) {
    saveProductTypeInCookie("Personal Loans");
    navigate("/login", {
      replace: true,
    });
  }

  // prospect Id from cookie
  const prospectAppId = getProspectAppIdFromCookie();

  // products list logic start
  const [productsList, setProductsList] = useState([]);
  const [subProductsList, setSubProductList] = useState([]);
  const [productsListLoading, setProductsListLoading] = useState(true);
  const [showSubCategoryModal, setShowSubCategoryModal] = useState(false);
  const [subcategoryTitle, setSubcategoryTitle] = useState("");
  const [showGSTModal, setShowGSTModal] = useState(false);
  const [isBackdropLoading, setIsBackdropLoading] = useState(true);
  const leadValue = getLeadFromCookie();
  const [search] = useSearchParams();
  const params = useParams();
  const [partnerId, setPartnerId] = useState(null);

  const getUTMs = () => {
    const utms = {};
    if (search.get("utm_id")) utms.utm_id = search.get("utm_id");
    if (search.get("utm_source")) utms.utm_source = search.get("utm_source");
    if (search.get("utm_medium")) utms.utm_medium = search.get("utm_medium");
    if (search.get("utm_campaign"))
      utms.utm_campaign = search.get("utm_campaign");
    if (search.get("utm_content")) utms.utm_content = search.get("utm_content");
    if (search.get("utm_term")) utms.utm_term = search.get("utm_term");
    if (search.get("pid")) utms.pid = search.get("pid");
    if (search.get("sub_id1")) utms.sub_id1 = search.get("sub_id1");
    if (search.get("sub_id2")) utms.sub_id2 = search.get("sub_id2");
    if (search.get("sub_id3")) utms.sub_id3 = search.get("sub_id3");
    return utms;
  };

  useEffect(() => {
    async function init() {
      if (isPartnerAvailable()) {
        await getPartnerIDByPartnerName(
          process.env.REACT_APP_PARTNER_API_NAME
        ).then((data) => {
          setPartnerId(data.id);
        });
      }
      saveUTMsCookie(getUTMs());
    }
    init();
  }, []);

  const urlSegment = isPrimaryDomain
    ? params.urlSegment || defaultUrlSegment
    : null;

  useEffect(() => {
    if (isPrimaryDomain) {
      if (urlSegment && urlSegment.match(urlSegmentRegex)) {
        saveUrlSegmentInCookie(urlSegment);
        // merchantQRCodeApi(urlSegment);
      } else {
        saveUrlSegmentInCookie(defaultUrlSegment);
      }
    }
  }, [urlSegment]);
  // urlSegment param end

  useEffect(() => {
    async function init() {
      if (leadValue) {
        saveProductTypeInCookie("Personal Loans");
        if (leadValue === "offers") {
          navigate("/user/welcome-back", {
            replace: true,
          });

          return;
        }
      }

      // Rupicard redirection start
      if (process.env.REACT_APP_SUBDOMAIN === "rupicard") {
        window.open(
          `/redirect?q=${encodeURIComponent(rupiCardURL)}&p=rupicard`,
          "_self"
        );
        return;
      }
      // Rupicard redirection end

      // Zetcar redirection start
      // if (process.env.REACT_APP_SUBDOMAIN === "zetcard") {
      //   window.open(`/redirect?q=${encodeURIComponent(zetcardURL)}&p=zetcard`, '_self')
      //   return;
      // }
      // Zetcar redirection end

      let partnerId = null;

      if (urlSegment && urlSegment !== defaultUrlSegment) {
        const data = await partnerIdByUrlSegment(urlSegment);
        partnerId = data;
      } else if (isPartnerAvailable()) {
        await getPartnerIDByPartnerName(
          process.env.REACT_APP_PARTNER_API_NAME
        ).then((data) => {
          partnerId = data.id;
        });
      }

      await productListApi(partnerId)
        .then(async (data) => {
          // AU Bank redirection start
          if (process.env.REACT_APP_SUBDOMAIN === "aubank") {
            const creditCard = data.find((item) =>
              ["credit-card"].includes(item.landing_url)
            );
            const auBankCreditCard = creditCard.subcategories.find((item) =>
              ["aubank"].includes(item.landing_url)
            );
            await axios
              .post(`${endpoint}/api/product-utm`, {
                category_id: auBankCreditCard.category_id,
                credit_prospect_uid: getProspectAppIdFromCookie(),
              })
              .then((response) => {
                const utms = response.data.data;
                if (utms.length > 0) {
                  window.open(
                    `/redirect?q=${encodeURIComponent(
                      utms[0].utm_link
                    )}&p=aubank`,
                    "_self"
                  );
                }
              });
            return;
          }
          // AU Bank redirection end

          // Zetcard redirection start
          if (process.env.REACT_APP_SUBDOMAIN === "zetcard") {
            const creditCard = data.find((item) =>
              ["credit-card"].includes(item.landing_url)
            );
            const cardSecured = creditCard.subcategories.find((item) =>
              ["cards-secured"].includes(item.landing_url)
            );
            const zetCard = cardSecured.subcategories.find((item) =>
              ["zet-card"].includes(item.landing_url)
            );

            await axios
              .post(`${endpoint}/api/product-utm`, {
                category_id: zetCard.category_id,
                credit_prospect_uid: getProspectAppIdFromCookie(),
              })
              .then((response) => {
                const utms = response.data.data;
                if (utms.length > 0) {
                  window.open(
                    `/redirect?q=${encodeURIComponent(
                      utms[0].utm_link
                    )}&p=zetcard`,
                    "_self"
                  );
                }
              });
            return;
          }
          // Zetcard redirection end

          // Health Insurance redirection start
          if (process.env.REACT_APP_SUBDOMAIN === "healthinsurance") {
            const insurance = data.find((item) =>
              ["insurance"].includes(item.landing_url)
            );
            const healthInsurance = insurance.subcategories.find((sub) =>
              ["health-insurance"].includes(sub.landing_url)
            );
            setProductsList(
              healthInsurance.subcategories.map((item) => {
                return {
                  id: item.category_id || null,
                  name: item.category_name || "",
                  productBannerUrl: item.category_img_thumb || "",
                  utm_link: item.utm_link || null,
                  is_subcategory: true,
                  subcategories: item.subcategories || [],
                  long_description: item.long_description || "",
                };
              })
            );
            setProductsListLoading(false);
            setIsBackdropLoading(false);
            return;
          }
          // Health Insurance redirection end

          // Credit Card redirection start
          if (process.env.REACT_APP_SUBDOMAIN === "creditcards") {
            const creditCard = data.find((item) =>
              ["credit-card"].includes(item.landing_url)
            );
            setProductsList(
              creditCard.subcategories.map((item) => {
                return {
                  id: item.category_id || null,
                  name: item.category_name || "",
                  productBannerUrl: item.category_img_thumb || "",
                  utm_link: item.utm_link || null,
                  is_subcategory: true,
                  subcategories: item.subcategories || [],
                  long_description: item.long_description || "",
                };
              })
            );
            setProductsListLoading(false);
            setIsBackdropLoading(false);
            return;
          }
          // Credit Card redirection end

          // Insurance redirection start
          if (process.env.REACT_APP_SUBDOMAIN === "insurance") {
            const insurance = data.find((item) => ['insurance'].includes(item.landing_url));
            setProductsList(insurance.subcategories.map((item) => {
              return {
                id: item.category_id || null,
                name: item.category_name || "",
                productBannerUrl: item.category_img_thumb || "",
                utm_link: item.utm_link || null,
                is_subcategory: true,
                subcategories: item.subcategories || [],
                long_description: item.long_description || ""
              };
            }))
            setProductsListLoading(false);
            setIsBackdropLoading(false)
            return;
          }
          // Insurance redirection end

          // Loans start
          if (productsType === "loans") {
            setProductsList(() => {
              return data.filter((item) =>
                ["loan", "business-loans"].includes(item.landing_url)
              );
            });
          } else if (productsType === "student-loans") {
            setProductsList(() => {
              return data.filter((item) =>
                ["student-loans"].includes(item.landing_url)
              );
            });
          } else if (productsType === "gold-loans") {
            setProductsList(() => {
              return data.filter((item) =>
                ["gold-loans"].includes(item.landing_url)
              );
            });
          } else {
            setProductsList(data);
          }

          setProductsListLoading(false);
          // Loans End
        })
        .catch((error) => {
          console.error(error);
          setProductsListLoading(false);
        });

      setIsBackdropLoading(false);
    }
    init();
  }, [urlSegment]);
  // products list logic end

  useEffect(() => {
    if (process.env.REACT_APP_SUBDOMAIN === "loans") {
      saveProductTypeInCookie("Personal Loans");
      if (isEditing === 1) {
        navigate("/user/user-detail", { replace: true });
      } else if (isEditing === 0) {
        navigate("/user/welcome-back", {
          replace: true,
        });
      }
    }

    if (process.env.REACT_APP_SUBDOMAIN === "goldloans") {
      saveProductTypeInCookie("Gold Loans");
      navigate("/user/welcome-back", {
        replace: true,
      });
    }

    if (process.env.REACT_APP_SUBDOMAIN === "housingloans") {
      saveProductTypeInCookie("Affordable Housing Loan");
      navigate("/user/welcome-back", {
        replace: true,
      });
    }

    if (process.env.REACT_APP_SUBDOMAIN === "businessloans") {
      saveProductTypeInCookie("Business Loans");
      if (isEditing === 1) {
        navigate("/user/business-detail", { replace: true });
      } else if (isEditing === 0) {
        navigate("/user/business-welcome-back", {
          replace: true,
        });
      }
    }
  }, []);

  // handle proceed with product start
  const [proceedBtnLoading, setProceedBtnLoading] = useState(-1);
  const isEditing = useSelector((state) => state.user.isEditing);
  const isPlComplete = useSelector((state) => state.user.pl_completed);
  const isBlComplete = useSelector((state) => state.user.bl_completed);
  const authToken = getJWTTokenFromCookie();

  const handleProceed = (e, val, idx) => {
    e.preventDefault();

    /*
    if (val.landing_url === 'credit-card') {
      saveProductTypeInCookie(val.name); // saving productType to cookie
      saveProductIdInCookie(val.id); // saving productID to cookie
      saveCreditCardDetailsInCookie(val.subcategories) // saving Credit Card Details to cookie
      navigate("/credit-cards", {
        state: val.subcategories
      })
      return;
    }
    */

    if (val.name === "Unsecured Cards") {
      saveProductTypeInCookie(val.name); // saving productType to cookie
      saveProductIdInCookie(val.id); // saving productID to cookie
      saveCreditCardDetailsInCookie(val.subcategories, "unsecured"); // saving Credit Card Details to cookie
      navigate("/cards-unsecured", {
        state: val.subcategories,
      });
      return;
    }

    if (val.name === "Secured Cards") {
      saveProductTypeInCookie(val.name); // saving productType to cookie
      saveProductIdInCookie(val.id); // saving productID to cookie
      saveCreditCardDetailsInCookie(val.subcategories, "secured"); // saving Credit Card Details to cookie
      navigate("/cards-secured", {});
      return;
    }

    if (val.subcategories.length > 0) {
      setShowSubCategoryModal(true);
      setSubcategoryTitle(val.name);
      const subcategories = val.subcategories.map((item) => {
        return {
          id: item.category_id || null,
          name: item.category_name || "",
          productBannerUrl: item.category_img_thumb || "",
          utm_link: item.utm_link || null,
          is_subcategory: true,
          subcategories: item.subcategories || [],
          long_description: item.long_description || "",
        };
      });
      setSubProductList(subcategories);
    } else {
      setProceedBtnLoading(val.id);
      // product selected is personal loan
      // setProceedBtnLoading(-1);
      saveProductTypeInCookie(val.name); // saving productType to cookie
      saveProductIdInCookie(val.id); // saving productID to cookie
      if (prospectAppId && authToken) {
        if (
          val.name === "Personal Loans" ||
          val.name === "Gold Loans" ||
          val.name === "Affordable Housing Loan"
        ) {
          setProceedBtnLoading(-1);
          return navigate("/user/welcome-back", {
            replace: true,
          });
        } else if (val.name === "Business Loans") {
          setProceedBtnLoading(-1);
          return navigate("/user/business-welcome-back", {
            replace: true,
          });
        }

        const requestBody = {
          category_id: val.id,
          credit_prospect_uid: prospectAppId,
        };
        axios
          .post(`${endpoint}/api/product-utm`, requestBody)
          .then((res) => res.data)
          .then((data) => {
            if (data && data.data && data.data[0]) {
              if (data.data[0].iframe === "yes") {
                const pathMapping = {
                  "Other Insurance": "insurance",
                  "Credit Cards": "credit-card",
                };
                return navigate(
                  `/user/${
                    pathMapping[val.name]
                  }/transaction?url=${encodeURIComponent(
                    data.data[0].utm_link
                  )}`,
                  {
                    replace: true,
                  }
                );
              } else {
                window.open(
                  `/redirect?q=${encodeURIComponent(data.data[0].utm_link)}&p=${
                    data.data[0].landing_url
                  }`
                );
                return navigate("/", {
                  replace: true,
                });
              }
            }
          })
          .catch((error) => {
            if (error.response) {
              customToast.error(error.response.data?.message);
            } else {
              customToast.error(error.message);
            }
          })
          .finally(() => {
            setProceedBtnLoading(-1);
          });
      } else {
        return navigate(isPrimaryDomain ? "/login" : "/", {
          replace: true,
        });
      }
      // if (val.name === "Personal Loans" || val.name === "Gold Loans") {
      // if (isEditing === 1) {
      //   // user haven't completed filling the user details
      //   return navigate("/user/user-detail", { replace: true });
      // } else if (isEditing === 0) {
      //   // user has completed filling the user details
      //   return navigate("/user/welcome-back", {
      //     replace: true,
      //   });
      // } else {
      //   return;
      // }
      // } else if (val.name === "Business Loans") {
      //   setProceedBtnLoading(-1);
      //   saveProductTypeInCookie(val.name);
      //   if (isBlComplete === 1) {
      //     // user has completed filling the user details
      //     return navigate("/user/business-welcome-back", {
      //       replace: true,
      //     });
      //   } else if (isBlComplete === 0) {
      //     setShowGSTModal(true);
      //     // return navigate("/user/business-detail", { replace: true });
      //     return;
      //   } else {
      //     return;
      //   }
      // }

      // const requestBody = {
      //   category_id: val.id,
      //   credit_prospect_uid: prospectAppId,
      // };
      // axios
      //   .post(`${endpoint}/api/product-utm`, requestBody)
      //   .then((res) => res.data)
      //   .then((data) => {
      //     setProceedBtnLoading(-1);
      //     saveProductTypeInCookie(val.name);

      //     if (data && data.data && data.data[0]) {
      //       if (data.data[0].iframe === "yes") {
      //         const pathMapping = {
      //           "Other Insurance": "insurance",
      //           "Credit Cards": "credit-card",
      //         };
      //         return navigate(
      //           `/user/${pathMapping[val.name]
      //           }/transaction?url=${encodeURIComponent(data.data[0].utm_link)}`,
      //           {
      //             replace: true,
      //           }
      //         );
      //       } else {
      //         window.open(`/redirect?q=${encodeURIComponent(data.data[0].utm_link)}&p=${data.data[0].landing_url}`);
      //       }
      //     }
      //   })
      //   .catch((error) => {
      //     setProceedBtnLoading(-1);
      //     if (error.response) {
      //       customToast.error(error.response.data?.message);
      //     } else {
      //       customToast.error(error.message);
      //     }
      //   });
    }
  };
  // handle procees with product end

  // Sub category modal style start
  const style = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    bgcolor: "background.paper",
    maxWidth: "1320px",
    width: "calc(100% - 24px)",
    boxShadow: 24,
    p: 4,
    borderRadius: 1,
    overflow: "auto",
  };
  // Sub category modal style end

  const cardStyle = (theme) => {
    return {
      display: "flex",
      gap: 4,
      cursor: "pointer",
      transition: "all 0.3s",
      height: "100%",
      "&:hover": {
        backgroundColor: "#07004e",
        color: "white",
      },
    };
  };

  // Product grid style start
  const productGrid = (option, idx) => {
    return (
      <Grid item key={idx} xs={12} sm={8} md={6}>
        <Card sx={{ height: { md: "100%" } }} variant="outlined">
          {productsListLoading ? (
            <Skeleton
              variant="rectangular"
              width="100%"
              height={220}
              animation="wave"
            />
          ) : (
            <Grid
              onClick={(e) => handleProceed(e, option, idx)}
              sx={cardStyle}
              p={2}
            >
              <CardMedia
                component="img"
                style={{ objectFit: "contain" }}
                image={option.productBannerUrl}
                alt={option.name}
                sx={{ width: { xs: 75, md: 100 }, cursor: "pointer" }}
                onClick={(e) => handleProceed(e, option, idx)}
              />
              <Grid>
                <Typography
                  mb={option.long_description && 2}
                  variant="h6"
                  fontWeight={500}
                >
                  {t(option.name)}
                </Typography>
                {option.long_description && (
                  <Typography>{t(option.long_description)}</Typography>
                )}
              </Grid>
              {/* <LoadingButton
                  size="large"
                  fullWidth
                  variant="contained"
                  onClick={(e) => handleProceed(e, option, idx)}
                  loading={proceedBtnLoading === option.id}
                  disabled={proceedBtnLoading !== -1}
                  color="secondary"
                  sx={{ fontWeight: "bold" }}
                >
                  {t(option.name)}
                </LoadingButton> */}
            </Grid>
          )}
        </Card>
      </Grid>
    );
  };
  // Product grid style end

  return (
    <>
      <Box sx={{ overflow: "auto", paddingY: 5, minHeight: "82vh" }}>
        <Helmet>
          <title>Products {app_title && `| ${app_title}`}</title>
        </Helmet>
        <HashLogin setLoading={setIsBackdropLoading} />
        {isBackdropLoading && <Loading />}
        {!isBackdropLoading && (
          <>
            <Grid
              container
              sx={{
                minWidth: 300,
                justifyContent: { xs: "initial", md: "center" },
              }}
            >
              <Grid container>
                {process.env.REACT_APP_SUBDOMAIN !== 'insurance' && <Grid item xs={12} mb={3}>
                  <Typography
                    variant="h6"
                    color="secondary"
                    align="center"
                    fontWeight="bold"
                    gutterBottom
                  >
                    {process.env.REACT_APP_SUBDOMAIN === 'healthinsurance' ? t("Healthinsurance Products") : t("Available Products")}
                  </Typography>
                </Grid>}
                <Grid container spacing={3} sx={{ justifyContent: "center" }}>
                  {!productsListLoading && productsList.length === 0 && (
                    <Grid item xs={12}>
                      <Typography align="center" variant="h6">
                        {t("No Products Available")}
                      </Typography>
                    </Grid>
                  )}
                  {(productsListLoading
                    ? Array.from(new Array(2))
                    : productsList
                  ).map((option, idx) => productGrid(option, idx))}
                </Grid>

                <Modal
                  open={showSubCategoryModal}
                  onClose={() => setShowSubCategoryModal(false)}
                  aria-labelledby="modal-modal-title"
                  aria-describedby="modal-modal-description"
                >
                  <Box sx={style}>
                    <Typography
                      id="modal-modal-title"
                      variant="h6"
                      component="h2"
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginBottom: 2,
                      }}
                    >
                      {t(subcategoryTitle)}
                      <IconButton
                        onClick={() => setShowSubCategoryModal(false)}
                      >
                        <CloseIcon />
                      </IconButton>
                    </Typography>
                    <Grid
                      container
                      spacing={3}
                      sx={{ marginTop: 2, justifyContent: "center" }}
                    >
                      {subProductsList.map((option, idx) =>
                        productGrid(option, idx)
                      )}
                    </Grid>
                  </Box>
                </Modal>
              </Grid>
            </Grid>
          </>
        )}
      </Box>

      <BussinessGSTModal
        open={showGSTModal}
        onClose={() => setShowGSTModal(false)}
      />
    </>
  );
}

export default Products;
